import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
  Upload,
} from "antd";
import { FileUp, Plus, PlusCircle, Trash2 } from "lucide-react";
import { userTable } from "@configurations";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useState } from "react";
import { ExamParticipantTable } from "../ExamParticipantTable";
import { ExamPeriod, ExamPeriodState } from "@types";
import { useEditExamPeriod, useAddParticipant, useAddFromCsv} from "@hooks";
import { useNavigate } from "react-router-dom";

interface Props {
  examPeriod: ExamPeriod;
  setActiveKey: Dispatch<SetStateAction<string>>;
}

export const ExamPeriodUsers = ({examPeriod, setActiveKey}: Props) => {
  const {t} = useTranslation();
  const { message } = App.useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const addParticipant = useAddParticipant();
  const editExamPeriod = useEditExamPeriod();
  const addFromCsv = useAddFromCsv();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const onFinish = (values: any) => {
    addParticipant.mutate(values, {
      onSuccess(data) {
          if (examPeriod.userExaminees.some((participant) => participant.id === data.id)) {
            message.info(t("messages.info.participantAlreadyInList"));
            form.resetFields();
            return;
          }

          editExamPeriod.mutate({
              ...examPeriod,
              // participants: [...examPeriod.participants, data],
              userExaminees: [...examPeriod.userExaminees, data]
          }, {
            onSuccess() {
              form.resetFields();
            }
          });
      },
    });
  }

  const uploadFile = async (options: any) => {
    const { onSuccess, onError, file } = options;
    
    addFromCsv.addCsvFile(file, examPeriod.id).then((res) => {      
      onSuccess(res, file);
    }).catch((err) => {
      onError({ err });
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      {examPeriod.state !== ExamPeriodState.InActive && (<Alert showIcon message={t("pages.examPeriods.applicantsTab.cannotAddUsers")} type="warning" style={{marginBottom: 10}} />)}
      
      <Row gutter={[16,16]}>
        <Col xxl={8} xl={8} lg={8} md={24} sm={24} xs={24}>
          <Card title={t("pages.examPeriods.applicantsTab.addUser")}>
            <Typography.Text strong>{t("pages.examPeriods.applicantsTab.addNewUser")}</Typography.Text>
            <Form form={form} layout="vertical" requiredMark onFinish={onFinish}>
              <Form.Item
                name={["firstName"]}
                label={t("pages.examPeriods.applicantsTab.userName")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={["lastName"]}
                label={t("pages.examPeriods.applicantsTab.lastName")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={["email"]}
                label={t("pages.examPeriods.applicantsTab.email")}
                rules={[{ required: true }, { type: "email" }]}
                normalize={(value) => value?.trim()}
              >
                <Input />
              </Form.Item>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button disabled={examPeriod.state !== ExamPeriodState.InActive} type="primary" htmlType="submit" loading={addParticipant.isPending} icon={<PlusCircle />}>
                {t("pages.examPeriods.applicantsTab.addToList")}
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
        <Col xxl={16} xl={16} lg={16} md={24} sm={24} xs={24}>
          <div
            style={{
              boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 5px",
              borderRadius: "8px",
            }}
          >
            <Flex
              style={{ padding: 16 }}
              justify="space-between"
              align="center"
            >
              <Typography.Title level={5} style={{ margin: 0 }}>
              {t("pages.examPeriods.applicantsTab.userList")}
              </Typography.Title>
              <Space>
              <Button disabled={examPeriod.state !== ExamPeriodState.InActive} id="new-user" type="primary" onClick={() => setIsModalOpen(true)} icon={<Plus />}>
                {t("pages.examPeriods.applicantsTab.addExistant")}
              </Button>
              <Upload customRequest={uploadFile} showUploadList={false}>
                <Button disabled={examPeriod.state !== ExamPeriodState.InActive} type="dashed" icon={<FileUp />}>
                  {t("pages.examPeriods.applicantsTab.importFromFile")}
                </Button>
              </Upload>
              <Popconfirm
                title={t("pages.examPeriods.applicantsTab.deleteUser")}
                disabled={selectedRowKeys.length == 0 || examPeriod?.state !== ExamPeriodState.InActive}
                onConfirm={() => {
                  editExamPeriod.mutate({
                    ...examPeriod,
                    userExaminees: examPeriod.userExaminees.filter((participant) => !selectedRowKeys.includes(participant.id))
                  }, {
                    onSuccess() {
                      setSelectedRowKeys([]);
                    }
                  })
                }}
                description={t("pages.examPeriods.applicantsTab.deleteUserDesc")}
                okButtonProps={{ loading: editExamPeriod.isPending }}
                icon={<Trash2 style={{ color: "red", marginRight: 8 }} />}
              >
                <Button type="primary" danger  icon={<Trash2 />}/>
              </Popconfirm>
              </Space>
            </Flex>
            <Table
              rowKey={(record) => record.id}
              dataSource={examPeriod.userExaminees}
              size="small"
              pagination={{
                showTotal: (total) => `${t("pages.examPeriods.applicantsTab.applicantsTotal")}: ${total}`
              }}
              rowSelection={{
                type: "checkbox",
                onChange: onSelectChange,
              }}
              columns={userTable(examPeriod)}
            />
          </div>
        </Col>
        <Col
          span={24}
          style={{
            display: "flex",
            flexDirection: "row-reverse",
            marginBottom: 16
          }}
        >
          <Space>
          <Button onClick={() => navigate(-1)}>{t("pages.examsManagment.cancel")}</Button>
            <Button type="dashed" disabled={ examPeriod !== undefined ? examPeriod?.state !== ExamPeriodState.InActive : false }>{t("pages.examsManagment.saveQuit")}</Button>
            <Button type="primary" htmlType="submit" onClick={() => setActiveKey("4")}>
            {t("pages.examsManagment.next")}
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal 
        footer={null}
        open={isModalOpen}
        width={1000}
        destroyOnClose
        onCancel={() => setIsModalOpen(false)}
        title={t("pages.examPeriods.applicantsTab.addUser")}
      >
        <ExamParticipantTable setIsModalOpen={setIsModalOpen} examPeriod={examPeriod}/>
      </Modal>
    </motion.div>
  );
};
